import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import SendIcon from "@mui/icons-material/Send";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";

export default function ChatWindow({ token, id_session, closeSession }) {
    const [messageInput, setMessageInput] = React.useState("");
    const [messages, setMessages] = React.useState([]);
    const [sending, setSending] = React.useState(false);
    const messagesEndRef = React.useRef(null);

    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const sendMessage = async () => {
        const outgoing = messageInput;
        setMessageInput("");
        setSending(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/session/message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Token": token
                },
                body: JSON.stringify({ id_session: id_session, message: outgoing }),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            setMessages((prevMessages) => [
                ...prevMessages,
                { author: "user", message: outgoing },
                { author: "system", message: "" },
            ]);

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let responseText = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                responseText += decoder.decode(value, { stream: true });

                setMessages((prevMessages) => {
                    const updatedMessages = [...prevMessages];
                    const lastMessageIndex = updatedMessages.length - 1;
                    updatedMessages[lastMessageIndex] = {
                        ...updatedMessages[lastMessageIndex],
                        message: responseText,
                    };
                    return updatedMessages;
                });
            }
        } catch (error) {
            console.error("Error sending message:", error);
        } finally {
            setSending(false);
        }
    };

    const handleSend = (e) => {
        e.preventDefault();
        if (messageInput.trim() && !sending) {
            sendMessage();
        }
    };

    const handleCloseSession = async () => {
        closeSession();

        await fetch(`${process.env.REACT_APP_API_URL}/session/close`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Token": token
            },
            body: JSON.stringify({ id_session: id_session }),
        });
    };

    return (
        <Stack sx={{ height: "70vh" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                    Session: {id_session}
                </Typography>
                <IconButton size="small" onClick={handleCloseSession} title="Close Session">
                    <Typography variant="button" color="error">Close Session</Typography>
                </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2 }}>
                <Stack spacing={1}>
                    {messages.map((msg, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                justifyContent: msg.author === "user" ? "flex-end" : "flex-start",
                            }}
                        >
                            <Stack
                                direction={msg.author === "user" ? "row-reverse" : "row"}
                                spacing={1}
                                alignItems="flex-end"
                                sx={{ maxWidth: "75%" }}
                            >
                                <Avatar sx={{ width: 28, height: 28, bgcolor: msg.author === "user" ? "primary.main" : "grey.500" }}>
                                    {msg.author === "user" ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
                                </Avatar>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        bgcolor: msg.author === "user" ? "primary.light" : "grey.100",
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                        {msg.message}
                                    </Typography>
                                </Paper>
                            </Stack>
                        </Box>
                    ))}
                    <div ref={messagesEndRef} />
                </Stack>
            </Box>
            <Box component="form" onSubmit={handleSend} sx={{ display: "flex", gap: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    disabled={sending}
                />
                <IconButton type="submit" color="primary" disabled={sending || !messageInput.trim()}>
                    <SendIcon />
                </IconButton>
            </Box>
        </Stack>
    );
}
